import cx from 'classnames';
import type {KeyboardEvent, MouseEvent, ReactNode} from 'react';
import React from 'react';
import {Reference} from 'react-popper';

import {useMenu} from './MenuContext.react';

interface MenuTriggerRenderProps {
  onClick: (e: MouseEvent) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  tabIndex: number;
}

interface MenuTriggerProps {
  children: (props: MenuTriggerRenderProps) => ReactNode;
  className?: string;
  fillWrapper?: boolean;
}

const MenuTrigger = ({children, className, fillWrapper = false}: MenuTriggerProps) => {
  const {toggle, isOpen} = useMenu();

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    toggle();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'Escape' && isOpen) {
      toggle();
    }
  };

  const triggerProps: MenuTriggerRenderProps = {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': isOpen,
    tabIndex: 0
  };

  return (
    <Reference>
      {({ref}) => (
        <div
          ref={ref}
          className={cx('o-menu-target', className, {
            'o-menu-target--fill': fillWrapper
          })}
        >
          {children(triggerProps)}
        </div>
      )}
    </Reference>
  );
};

export default MenuTrigger;
